/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description store for eligibility requests
 */
import { create } from 'zustand';
import _ from 'lodash';
import { EXPORT_OPTIONS, dateType } from 'utils/eligibility/eligibilityRequests';
import { DATE_PRESETS, covertDateToStringArray } from 'utils/dateParser';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { DEFAULT_PAGINATION } from '@veritable/common/constants';

const defaultExportMetaData = {
	visible: false,
	dateRange: { key: dateType.radio, range: EXPORT_OPTIONS[0].value },
};

const defaultBatchResubmitMetaData = {
	visible: false,
	dateRange: { key: dateType.radio, range: EXPORT_OPTIONS[0].value },
	newDateRange: covertDateToStringArray(DATE_PRESETS.today.value),
};

const initialState: EligibilityRequests.IValues = {
	...DEFAULT_PAGINATION,
	searchParams: {},
	apiParams: {},
	exportMetaData: defaultExportMetaData,
	batchResubmitMetaData: defaultBatchResubmitMetaData,
};

const createEligibilityStore = (storageName: string, storageKey: string) =>
	create<EligibilityRequests.IStore>()(
		persist(
			devtools(
				(set) => ({
					...initialState,
					actions: {
						// TODO : handle reset states
						resetStates: () => set(() => ({})),
						setPageSize: (pageSize) => set(() => ({ pageSize })),
						setPageNumber: (pageNumber) => set(() => ({ pageNumber })),
						setSearchParams: (searchParams) =>
							set(() => ({ searchParams: _.isEmpty(searchParams.filterParams) ? null : searchParams })),
						setApiParams: (apiParams) =>
							set(() => ({
								apiParams: {
									...apiParams,
									criteria: _.isEmpty(apiParams?.criteria) ? null : apiParams?.criteria,
								},
							})),
						setExportMetaData: (exportMetaData) => set(() => ({ exportMetaData })),
						setBatchResubmitMetaData: (batchResubmitMetaData) => set(() => ({ batchResubmitMetaData })),
						closeExportModal: () => set(() => ({ exportMetaData: defaultExportMetaData })),
						closeBatchResubmissionModal: () => set(() => ({ batchResubmitMetaData: defaultBatchResubmitMetaData })),
					},
				}),
				{ name: storageName },
			),
			{
				name: storageKey,
				storage: createJSONStorage(() => sessionStorage),
				partialize: (state) => ({
					pageSize: state.pageSize,
					searchParams: state.searchParams,
					apiParams: state.apiParams,
				}),
			},
		),
	);

const useEligibilityStore = createEligibilityStore('Eligibility Requests', 'eligibility-requests');
const useBatchEligibilityStore = createEligibilityStore('Batch Eligibility Requests', 'batch-eligibility-requests');

// store to return actions and values for eligibility
const useEligibilityStoreValues = (): EligibilityRequests.IValues => useEligibilityStore(useShallow((state) => _.omit(state, ['actions'])));
const useEligibilityStoreActions = (): EligibilityRequests.IActions => useEligibilityStore(useShallow((state) => state.actions));

const useBatchEligibilityStoreValues = (): EligibilityRequests.IValues =>
	useBatchEligibilityStore(useShallow((state) => _.omit(state, ['actions'])));
const useBatchEligibilityStoreActions = (): EligibilityRequests.IActions => useBatchEligibilityStore(useShallow((state) => state.actions));

export {
	DEFAULT_PAGINATION as defaultPagination,
	useEligibilityStoreValues,
	useEligibilityStoreActions,
	useBatchEligibilityStoreValues,
	useBatchEligibilityStoreActions,
};
